import React, { useEffect, useState } from 'react';
import { Row, Col, Spin, Button, Modal, Form, Card, Typography, Tag, InputNumber, Select, Upload, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import TaxonomyPicker from '../../components/TaxonomyPicker';
import SettingsPillBadge from '../../components/Settings/SettingsPillBadge';
import {
  getMyOperators,
  createOperator,
  updateOperator,
  deleteOperator,
  uploadOperatorDocument,
  downloadOperatorDocument,
} from '../../services/OperatorService';
import { IOperator } from '../../interfaces/heavyDutyMachinery/IOperator';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { pillToneStyle } from '../../theme/creamyTokens';

const { Text, Title } = Typography;

const DOCUMENT_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'OperatorLicense', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_TYPE_LICENSE },
  { value: 'PRLCertificate', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_TYPE_PRL },
  { value: 'Other', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_TYPE_OTHER },
];

interface OperatorFormValues {
  fullName: string;
  email: string;
  phone?: string;
  hourlyRate: number;
  qualifiedCategories: string[];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A host's own staff roster for "wet hire, host-operated" bookings (Fase
 * 4ter) -- OperatorController's CRUD + document upload has existed since
 * Fase 4, but never had a management screen until now. Same "catalog at the
 * host level" list-plus-create pattern as MyMachineryAccessories/
 * MyHostInsurancePolicies, added to HOST_TOOLS_NAV's own host-only nav as a
 * peer to "Mi flota"/"Mis accesorios"/"Mi seguro".
 * Unlike those two, an Operator isn't linked to a specific Machinery here --
 * QualifiedCategories already decides which machines it can be offered on
 * at checkout (see MachineryController.GetAvailableOperators), so there's
 * no separate link/unlink section anywhere.
 */
const MyOperatorsContent: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { resolveName: categoryLabel } = useTaxonomy();
  const [operators, setOperators] = useState<IOperator[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<IOperator | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [documentType, setDocumentType] = useState<string>('OperatorLicense');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [deletingOperator, setDeletingOperator] = useState<IOperator | undefined>(undefined);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [form] = Form.useForm<OperatorFormValues>();

  const load = () => {
    setLoading(true);
    getMyOperators()
      .then(setOperators)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_LOAD_ERROR)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateModal = () => {
    setEditingOperator(undefined);
    setDocumentType('OperatorLicense');
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (operator: IOperator) => {
    setEditingOperator(operator);
    setDocumentType('OperatorLicense');
    form.setFieldsValue({
      fullName: operator.fullName,
      email: operator.email,
      phone: operator.phone,
      hourlyRate: operator.hourlyRate,
      qualifiedCategories: operator.qualifiedCategories.split(',').filter(Boolean),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = {
      fullName: values.fullName.trim(),
      email: values.email.trim(),
      phone: values.phone?.trim() || undefined,
      hourlyRate: values.hourlyRate,
      qualifiedCategories: values.qualifiedCategories.join(','),
    };

    setSaving(true);
    try {
      const saved = editingOperator ? await updateOperator(editingOperator.id, payload) : await createOperator(payload);
      setOperators((prev) => (editingOperator ? prev.map((o) => (o.id === saved.id ? saved : o)) : [saved, ...prev]));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_SAVE_SUCCESS));
      // Deliberately doesn't close the modal (unlike MyMachineryAccessories/
      // MyHostInsurancePolicies) -- staying open on the now-saved operator
      // means a first-time Save immediately reveals the documents section
      // below (OperatorController.UploadDocument needs an existing
      // operatorId), so a host can upload a license right after creating one
      // without a second click into Edit.
      setEditingOperator(saved);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (operator: IOperator) => {
    try {
      const updated = await updateOperator(operator.id, { isActive: !operator.isActive });
      setOperators((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_STATUS_UPDATE_ERROR));
    }
  };

  const handleUploadDocument = async (file: File) => {
    if (!editingOperator) return false;
    setUploadingDocument(true);
    try {
      const document = await uploadOperatorDocument(editingOperator.id, documentType, file);
      const updated = { ...editingOperator, documents: [...editingOperator.documents, document] };
      setEditingOperator(updated);
      setOperators((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_UPLOAD_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_UPLOAD_ERROR));
    } finally {
      setUploadingDocument(false);
    }
    return false;
  };

  const handleDownloadDocument = async (documentId: string) => {
    if (!editingOperator) return;
    const document = editingOperator.documents.find((d) => d.id === documentId);
    if (!document) return;
    try {
      await downloadOperatorDocument(editingOperator.id, document);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_DOWNLOAD_ERROR));
    }
  };

  const handleDeleteOperator = async () => {
    if (!deletingOperator) return;
    setDeleteBusy(true);
    try {
      await deleteOperator(deletingOperator.id);
      setOperators((prev) => prev.filter((o) => o.id !== deletingOperator.id));
      setDeletingOperator(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_STATUS_UPDATE_ERROR));
    } finally {
      setDeleteBusy(false);
    }
  };

  const documentTypeLabel = (value: string) => translate(t, DOCUMENT_TYPE_OPTIONS.find((o) => o.value === value)?.labelKey ?? value);

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_NAV_LABEL)}
            </Title>
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_PAGE_SUBTITLE)}</Text>
          </div>
          {!loading && operators.length > 0 && (
            <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={openCreateModal}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_CREATE_BUTTON)}
            </Button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : operators.length === 0 ? (
          <EmptyState
            icon={<IconUsers size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_EMPTY_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_EMPTY_DESCRIPTION)}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_CREATE_BUTTON)}
              </Button>
            }
          />
        ) : (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {operators.map((operator) => (
              <Col xs={24} sm={12} md={8} key={operator.id}>
                <Card bordered style={{ borderRadius: 18, opacity: operator.isActive ? 1 : 0.6 }} styles={{ body: { padding: 16 } }}>
                  <span
                    style={{
                      display: 'inline-block',
                      alignSelf: 'flex-start',
                      marginBottom: 4,
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      ...pillToneStyle(operator.isActive ? 'success' : 'default', token),
                    }}
                  >
                    {translate(t, operator.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ACTIVE_STATUS : TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_INACTIVE_STATUS)}
                  </span>

                  <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {operator.fullName}
                  </Title>
                  {/* Nothing is shown when all is well: an operator on the
                      roster is simply usable. The only state worth a badge
                      is the platform having refused to carry them, which is
                      rare and which the host must not have to guess at. */}
                  {operator.suspendedAt && (
                    <div style={{ marginTop: 8 }}>
                      <SettingsPillBadge tone="error">
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_SUSPENDED_BADGE)}
                      </SettingsPillBadge>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        {operator.suspensionReason
                          ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_SUSPENDED_NOTICE, { reason: operator.suspensionReason })
                          : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_SUSPENDED_NOTICE_NO_REASON)}
                      </Text>
                    </div>
                  )}
                  <Text strong style={{ display: 'block', marginTop: 10, fontSize: 15 }}>
                    {operator.hourlyRate.toFixed(2)}€/h
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {operator.qualifiedCategories.split(',').filter(Boolean).map((cat) => (
                      <Tag key={cat} style={{ borderRadius: 999, margin: 0 }}>{categoryLabel(cat)}</Tag>
                    ))}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(operator)}
                      >
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_EDIT_BUTTON)}
                      </Button>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle(operator.isActive ? 'error' : 'success', token) }}
                        icon={operator.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                        onClick={() => toggleActive(operator)}
                      >
                        {translate(t, operator.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DEACTIVATE_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_REACTIVATE_BUTTON)}
                      </Button>
                    </div>
                    <Button
                      shape="round"
                      block
                      style={{ height: 40, border: 'none', ...pillToneStyle('error', token) }}
                      icon={<DeleteOutlined />}
                      onClick={() => setDeletingOperator(operator)}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_BUTTON)}
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal
          open={modalOpen}
          title={translate(t, editingOperator ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_MODAL_EDIT_TITLE : TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_MODAL_CREATE_TITLE)}
          onCancel={() => setModalOpen(false)}
          onOk={handleSave}
          confirmLoading={saving}
          okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_SAVE_BUTTON)}
          okButtonProps={{ shape: 'round' }}
          cancelButtonProps={{ shape: 'round' }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="fullName" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_NAME_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) },
                { pattern: EMAIL_PATTERN, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_EMAIL_INVALID) },
              ]}
            >
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_EMAIL_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="phone">
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_PHONE_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_HOURLY_RATE_LABEL)} name="hourlyRate" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <InputNumber min={0} step={1} addonAfter="€/h" inputMode="decimal" style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Form.Item
              label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_QUALIFIED_CATEGORIES_LABEL)}
              name="qualifiedCategories"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}
            >
              <TaxonomyPicker multiple disabled={saving} />
            </Form.Item>
          </Form>

          <div style={{ marginTop: 8, paddingTop: 16, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
            <Title level={5} style={{ marginBottom: 2 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENTS_TITLE)}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENTS_HINT)}
            </Text>

            {!editingOperator ? (
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 12, fontStyle: 'italic' }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_SAVE_FIRST_HINT)}
              </Text>
            ) : (
              <>
                {editingOperator.documents.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 12 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_EMPTY)}
                  </Text>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    {editingOperator.documents.map((document) => (
                      <div
                        key={document.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          padding: '8px 12px',
                          borderRadius: 14,
                          background: token.colorFillAlter,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <Text strong style={{ display: 'block', fontSize: 13 }}>{documentTypeLabel(document.documentType)}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(document.uploadedAt).toLocaleDateString()}
                          </Text>
                        </div>
                        <Button size="small" shape="circle" icon={<DownloadOutlined />} onClick={() => handleDownloadDocument(document.id)} />
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <Select
                    value={documentType}
                    onChange={setDocumentType}
                    style={{ minWidth: 180 }}
                    options={DOCUMENT_TYPE_OPTIONS.map((option) => ({ value: option.value, label: translate(t, option.labelKey) }))}
                    disabled={uploadingDocument}
                  />
                  <Upload
                    accept="application/pdf,image/jpeg,image/png"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleUploadDocument(file);
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />} loading={uploadingDocument} shape="round">
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_UPLOAD_BUTTON)}
                    </Button>
                  </Upload>
                </div>
              </>
            )}
          </div>
        </Modal>
      </div>

      <ConfirmBottomSheet
        open={!!deletingOperator}
        title={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_TITLE)}
        description={deletingOperator?.fullName}
        cancelText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_KEEP)}
        confirmText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_DELETE)}
        confirmLoading={deleteBusy}
        onConfirm={handleDeleteOperator}
        onClose={() => setDeletingOperator(undefined)}
      />
    </AppLayout>
  );
};

// Split into a wrapper + Content component so that a scoped theme can
// be added here without moving anything: theme.useToken() only sees a
// nested ConfigProvider's tokens from a component mounted UNDER it, not
// from the one rendering the provider. There is nothing scoped here
// today -- coral is the app-wide primary (see theme/appTheme).
const MyOperators: React.FC = () => {
  const { isMobile } = useAppBreakpoint();
  return isMobile ? (
    <MyOperatorsContent />
  ) : (
    <MyOperatorsContent />
  );
};

export default MyOperators;
